<template>
  <b-card title="Modifier un ramasseur">
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
    >
      <validation-observer
        ref="formValidator"
        #default="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(submitForm)">
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
              />
              Informations personnelles
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Nom du ramasseur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        v-model="formData.username"
                        :state="errors.length > 0 ? false : (valid && formData.username ? true : null)"
                        placeholder="Entrez le nom complet"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email (Read-only) -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Email">
                  <b-form-input class="premium-input"
                    v-model="formData.email"
                    type="email"
                    disabled
                  />
                </b-form-group>
              </b-col>

              <!-- Phone -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Téléphone <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Téléphone"
                    :rules="`required|tnPhone|asyncPhone:,${pickupeurId}`"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="formData.phone"
                        :formatter="formatPhone"
                        @keypress="allowOnlyNumbers"
                        :state="errors.length > 0 ? false : (valid && formData.phone ? true : null)"
                        placeholder="Entrez le numéro de téléphone"
                        maxlength="8"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone 2 -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Téléphone secondaire (optionnel)">
                  <b-form-input class="premium-input"
                    id="phone2"
                    v-model="formData.phone2"
                    :formatter="formatPhone"
                    @keypress="allowOnlyNumbers"
                    placeholder="Entrez le numéro de téléphone secondaire"
                    maxlength="8"
                  />
                </b-form-group>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Adresse">
                  <b-form-textarea
                    v-model="formData.address"
                    placeholder="Entrez l'adresse"
                    rows="3"
                  />
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Véhicule">
                  <v-select class="premium-input"
                    v-model="formData.car"
                    :options="carOptions"
                    label="displayName"
                    :reduce="car => car.id"
                    placeholder="Sélectionnez un véhicule (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>

              <!-- Repository -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium" label="Dépôt">
                  <v-select class="premium-input"
                    v-model="formData.repository"
                    :options="repositories"
                    label="name"
                    :reduce="repo => repo.id"
                    placeholder="Sélectionnez un dépôt (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-3">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'pickupeur-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                Annuler
              </b-button>

              <span id="pickupeur-edit-save-btn-wrapper" class="d-inline-block">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="!isFormValid || isSubmitting"
                  :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                  />
                  Enregistrer
                </b-button>
              </span>
              <b-tooltip
                v-if="!isFormValid || isSubmitting"
                target="pickupeur-edit-save-btn-wrapper"
                triggers="hover"
                placement="top"
              >
                {{ tooltipMessage }}
              </b-tooltip>
            </div>
          </b-card>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-card>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, numeric, length,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BOverlay,
  BFormTextarea,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

extend('required', required)
extend('numeric', numeric)
extend('length', length)

export default {
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BOverlay,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      cars: [],
      repositories: [],
      formData: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        car: null,
        repository: null,
      },
    }
  },

  computed: {
    pickupeurId() {
      return this.$route.params.id
    },
    carOptions() {
      if (!Array.isArray(this.cars)) return []
      return this.cars.map(car => ({
        ...car,
        displayName: `${car.brand || 'N/A'} - ${car.serial_number || 'N/A'} (${car.color || 'N/A'})`,
      }))
    },

    isFormValid() {
      if (!this.formData) return false
      return (
        this.formData.username !== ''
        && this.formData.phone !== ''
      )
    },

    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'
      if (!this.formData) return 'Chargement des données...'

      const missing = []
      if (!this.formData.username || !this.formData.username.trim()) missing.push('Nom')
      if (!this.formData.phone || !this.formData.phone.trim()) missing.push('Téléphone')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([
        this.fetchPickupeur(),
        this.fetchCars(),
        this.fetchRepositories(),
      ])
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      if (target && target.id === 'phone' && (this.formData?.phone || this.phone)?.length >= 8) {
        event.preventDefault()
      }
      if (target && target.id === 'phone2' && (this.formData?.phone2 || this.phone2)?.length >= 8) {
        event.preventDefault()
      }
    },
    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchPickupeur() {
      try {
        const response = await axios.get(`/api/auth/users/pickupeurs/${this.pickupeurId}/`)
        const { data } = response

        this.formData = {
          username: data.username || '',
          email: data.email || '',
          phone: data.phone || '',
          phone2: data.phone2 || '',
          address: data.address || '',
          car: data.car?.id || data.car || null,
          repository: data.repository || null,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickupeur:', error)
        this.showToast('danger', 'Erreur lors du chargement du ramasseur')
        this.$router.push({ name: 'pickupeur-list' })
      }
    },

    async fetchRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        if (Array.isArray(response.data)) {
          this.repositories = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          this.repositories = response.data.results
        }
      } catch (error) {
        this.repositories = []
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/')
        let cars = []
        if (Array.isArray(response.data)) {
          cars = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          cars = response.data.results
        }
        this.cars = cars
      } catch (error) {
        this.cars = []
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    async submitForm() {
      this.isSubmitting = true

      try {
        const payload = {
          username: this.formData.username,
          phone: this.formData.phone,
          phone2: this.formData.phone2,
          address: this.formData.address,
          car: this.formData.car,
          repository: this.formData.repository,
        }

        await axios.put(`/api/auth/users/pickupeurs/update/${this.pickupeurId}/`, payload)

        this.showToast('success', 'Ramasseur modifié avec succès')
        this.$router.push({ name: 'pickupeur-list' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating pickupeur:', error)

        let errorMessage = 'Erreur lors de la modification du ramasseur'

        if (error.response && error.response.data) {
          const errorData = error.response.data
          if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          }
        }

        this.showToast('danger', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #0d4399;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
